/**
 * useBlockStorage - Hook para bloquear storages individuales
 * 
 * Permite bloquear un storage específico enviando motivo de bloqueo y comentario.
 * Realiza POST a /invoices/storages/{id}/block con validación de campos requeridos
 * (storage_block_reason_id y comment). Muestra toasts de éxito/error, cierra el modal
 * y ejecuta callback de refetch al completar exitosamente.
 * 
 * @returns {Object} { blockStorage: función de bloqueo, isBlocking: estado de carga }
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@shared/hooks/use-toast';
import { EligibleStorageApiAdapter } from '../adapters/EligibleStorageApiAdapter';
import { apiClientWH } from '@shared/services/ApiClient';
import { createHttpClient } from '@adapters/HttpClient';

export const useBlockStorage = () => {
  const { t } = useTranslation('eligibleStorageModule');
  const { toast } = useToast();
  const [isBlocking, setIsBlocking] = useState(false);

  const blockStorage = useCallback(
    async (
      storageId: number,
      onSuccess?: () => void,
      data?: { storage_block_reason_id?: number | string; comment?: string }
    ) => {
      setIsBlocking(true);

      try {
        const storagesAdapter = new EligibleStorageApiAdapter(
          createHttpClient(apiClientWH)
        );
        await storagesAdapter.blockStorage(storageId, data);

        // 🎯 Si llegamos aquí, la API devolvió 200 OK = éxito
        toast({
          title: t('toast.block_success.title', 'Storage bloqueado'),
          description: t('toast.block_success.description', 'El storage se ha bloqueado exitosamente'),
          variant: 'default',
        });

        // 🎯 Ejecutar callback de éxito (refetch de datos)
        onSuccess?.();
      } catch (error: any) {
        // 🎯 Extraer mensaje amigable de la API
        const apiMessage: string | undefined = error?.response?.data?.message;
        const apiErrors: Record<string, string[] | string> | undefined =
          error?.response?.data?.errors;

        let description = apiMessage;

        // Unir los errores detallados si existen
        if (apiErrors && typeof apiErrors === 'object') {
          const details = Object.values(apiErrors)
            .flat()
            .filter(Boolean)
            .join(' • ');
          description = description ? `${description} — ${details}` : details;
        }

        // Fallback de mensaje
        if (!description) {
          description =
            error?.message ||
            t(
              'toast.block_error.unexpected',
              'Error inesperado al bloquear el storage. Inténtalo de nuevo.'
            );
        }

        toast({
          title: t('toast.block_error.title', 'Error al bloquear'),
          description,
          variant: 'destructive',
        });
      } finally {
        setIsBlocking(false);
      }
    },
    [t, toast]
  );

  return {
    blockStorage,
    isBlocking,
  };
};

